import { Link } from 'react-router-dom';
import { BadgeCheck, Crown, User } from 'lucide-react';
import PlateVisual from '../plates/PlateVisual';
import PriceDisplay from './PriceDisplay';
import { getDisplayPlateNumber } from '../../utils/digitMask';
import { cardFrame, riseDelay, GOLD_BADGE } from './frames';

export const EMIRATES = {
  dubai:          { ar: 'دبي',        en: 'Dubai' },
  abu_dhabi:      { ar: 'أبوظبي',     en: 'Abu Dhabi' },
  sharjah:        { ar: 'الشارقة',    en: 'Sharjah' },
  ajman:          { ar: 'عجمان',      en: 'Ajman' },
  ras_al_khaimah: { ar: 'رأس الخيمة', en: 'Ras Al Khaimah' },
  umm_al_quwain:  { ar: 'أم القيوين', en: 'Umm Al Quwain' },
  fujairah:       { ar: 'الفجيرة',    en: 'Fujairah' },
};

/** PlateCard — Cyber-Luxe car-plate card: emirate pill → official plate on a dotted stage → price/store. */
export default function PlateCard({ item, isDarkMode, isAr, plateWidth, index = 0, testIdPrefix = 'plate-card' }) {
  const em = EMIRATES[item.emirate];
  const isPersonal = item.is_personal || item.seller_type === 'personal';
  return (
    <Link
      to={`/plate/${item.id}`}
      state={{ store: item.store, item }}
      className={`group relative block rounded-3xl p-3 sm:p-5 overflow-hidden transition-all duration-300 hover:-translate-y-1 cgm-shimmer cgm-rise ${cardFrame(isDarkMode)}`}
      style={riseDelay(index)}
      data-testid={`${testIdPrefix}-${item.id}`}
    >
      <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-[#1E9BD9] via-[#38BDF8] to-[#F5A962]" />

      <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4">
        <span className={`px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-bold truncate max-w-[60%] border ${isDarkMode ? 'bg-[#1E9BD9]/12 text-[#38BDF8] border-[#1E9BD9]/30' : 'bg-[#1E9BD9]/10 text-[#0284C7] border-[#1E9BD9]/30'}`}>
          {em ? (isAr ? em.ar : em.en) : item.emirate}
        </span>
        <div className="flex items-center gap-1">
          {item.featured && (
            <span className={GOLD_BADGE}><Crown className="h-3 w-3" />{isAr ? 'مميز' : 'VIP'}</span>
          )}
          {isPersonal && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#F5A962]/15 text-[#F59E0B] border border-[#F5A962]/30" data-testid={`personal-badge-${item.id}`}>
              <User className="h-2.5 w-2.5" />{isAr ? 'شخصي' : 'Personal'}
            </span>
          )}
          {item.store?.verified && <BadgeCheck className="h-4 w-4 text-[#1E9BD9]" />}
        </div>
      </div>

      <div className={`w-full flex items-center justify-center py-3 sm:py-4 rounded-2xl border overflow-hidden ${isDarkMode ? 'cgm-stage-dark border-white/5' : 'cgm-stage-light border-slate-100'}`}>
        <div className="transition-transform duration-500 group-hover:scale-[1.04] drop-shadow-[0_10px_20px_rgba(0,0,0,0.25)]">
          <PlateVisual
            emirate={item.emirate}
            category={item.plateType}
            code={item.plateCode}
            number={getDisplayPlateNumber(item) || item.plateNumber}
            width={plateWidth}
            showCodeOnPlate={item.show_on_plate}
          />
        </div>
      </div>

      <div className={`mt-3 sm:mt-4 pt-3 flex items-center justify-between gap-2 border-t ${isDarkMode ? 'border-white/5' : 'border-slate-100'}`}>
        <PriceDisplay item={item} locale={isAr ? 'ar' : 'en'} size="sm" />
        <span className={`text-[10px] sm:text-xs truncate max-w-[80px] sm:max-w-[140px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          {item.store?.name || (isPersonal ? (isAr ? 'إعلان شخصي' : 'Personal ad') : '—')}
        </span>
      </div>
    </Link>
  );
}
